import React, { useEffect, useState } from "react";
import useScrollToHash from "../components/hooks/useScrollToHash";
import { Form, Input, Button, message } from "antd";
import CommonService from "../services/CommonService";

// Ant Design Icons
import {
  LinkedinFilled,
  TwitterOutlined,
  YoutubeFilled,
  ReadOutlined,
  EnvironmentFilled,
  MessageFilled
} from "@ant-design/icons";

// Import your external CSS file here
// import "./contact.css"; 

const ContactContainer = () => {
  useScrollToHash(200);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        email: values?.email,
        fullName: values?.fullName,
        phoneNumber: values?.phone,
        subject: values?.subject,
        dEscription: values?.message
      };

      const response = await CommonService.sendContactUs(payload);

      if (response?.status === 200 || response?.status === 201) {
        message.success(response?.data?.message || "Message sent successfully");
        form.resetFields();
      }
    } catch (error) {
      message.error("Something went wrong. Please try again.");
      console.error("Error during contact request", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="contact-section">
      <div className="container">
        {/* ===== Header ===== */}
        <div className="header-wrap">
          <h2 className="contact-heading">
            We’re Here to Help You Transform Education
          </h2>
          <p className="contact-subheading">
            At <strong>Stusphere</strong>, we partner with institutions,
            educators, and organizations to deliver scalable, future-ready
            education solutions. Our team is ready to support you at every step.
          </p>
        </div>

        <div className="card-grid">
          {/* Card 1 */}
          <div className="info-card contact-hover-card" style={{ backgroundColor: "#fae1dd" }}>
            <img
              src="images/resource/contact-1.jpg"
              alt=""
              className="card-img"
            />
            <div className="card-content">
              <h5 className="card-title">General Inquiries & Sales</h5>
              <p className="card-text">
                Product details, pricing plans, tailored demos, and onboarding
                guidance.
              </p>
              <a href="mailto:sales@stusphere.com" className="card-link" style={{ color: "#0a58ca", fontWeight: "600" }}>
                sales@stusphere.com
              </a>
              <p className="card-meta" style={{ fontSize: "15px", color: "#777", marginTop: "10px" }}>
                <a href="tel:+15551234567" style={{ color: "inherit" }}>+1 (555) 123-4567</a> · Mon–Fri
              </p>
            </div>
          </div>

          {/* Card 2 */}
          <div className="info-card contact-hover-card" style={{ backgroundColor: "#e9fbf8" }}>
            <img
              src="images/resource/contact-2.jpg"
              alt=""
              className="card-img"
            />
            <div className="card-content">
              <h5 className="card-title">Technical Support</h5>
              <p className="card-text">
                Platform assistance, integrations, performance issues, and
                upgrades.
              </p>
              <a href="mailto:support@stusphere.com" className="card-link" style={{ color: "#0a58ca", fontWeight: "600" }}>
                support@stusphere.com
              </a>
              <p className="card-meta" style={{ fontSize: "15px", color: "#777", marginTop: "10px" }}>24/7 for Pro & Enterprise plans</p>
            </div>
          </div>

          {/* Card 3 */}
          <div className="info-card contact-hover-card" style={{ backgroundColor: "#eaf4ff" }}>
            <img
              src="images/resource/contact-3.jpg"
              alt=""
              className="card-img"
            />
            <div className="card-content">
              <h5 className="card-title">Partnerships & Integrations</h5>
              <p className="card-text">
                Strategic collaborations, APIs, and ecosystem partnerships.
              </p>
              <a
                href="mailto:partnerships@stusphere.com"
                className="card-link"
                style={{ color: "#0a58ca", fontWeight: "600" }}
              >
                partnerships@stusphere.com
              </a>
            </div>
          </div>

          {/* Card 4 */}
          <div className="info-card contact-hover-card" style={{ backgroundColor: "#f3edff" }}>
            <img
              src="images/resource/billing.jpg"
              alt=""
              className="card-img"
            />
            <div className="card-content">
              <h5 className="card-title">Billing & Accounts</h5>
              <p className="card-text">
                Invoices, subscriptions, renewals, and account management.
              </p>
              <a href="mailto:billing@stusphere.com" className="card-link" style={{ color: "#0a58ca", fontWeight: "600" }}>
                billing@stusphere.com
              </a>
            </div>
          </div>
        </div>

        {/* ===== Contact Form ===== */}
        <div className="form-wrap" id="Contact-form">
          <h3 className="form-title">Send Us a Message</h3>
          <p className="form-sub">
            Our team will review your request and respond within one business
            day.
          </p>

          <Form form={form} layout="vertical" onFinish={onFinish}>
            <div className="contact-cards">
              <Form.Item
                label="Full Name"
                name="fullName"
                rules={[
                  { required: true, message: "Please enter your full name" },
                ]}
              >
                <Input className="contact-input" placeholder="Full Name *" />
              </Form.Item>

              <Form.Item
                label="Email Address"
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please enter your email address",
                  },
                  { type: "email", message: "Enter a valid email address" },
                ]}
              >
                <Input
                  type="email"
                  className="contact-input"
                  placeholder="Email Address *"
                />
              </Form.Item>

              <Form.Item label="Phone Number" name="phone">
                <Input className="contact-input" placeholder="Phone Number" />
              </Form.Item>

              <Form.Item label="Subject" name="subject">
                <Input className="contact-input" placeholder="Subject" />
              </Form.Item>

              <Form.Item
                label="Message"
                name="message"
                rules={[
                  { required: true, message: "Please describe your request" },
                ]}
                style={{ gridColumn: "1 / -1" }}
              >
                <Input.TextArea
                  className="contact-textarea"
                  placeholder="Describe your request *"
                />
              </Form.Item>
            </div>

            <div style={{ textAlign: "center" }}>
              <Button
                htmlType="submit"
                size="large"
                className="submit-btn"
                loading={loading}
              >
                Submit Enquiry
              </Button>
            </div>
          </Form>
        </div>

        {/* ===== Location & Social ===== */}
        <section className="new-footer-section">
          <div className="row g-4 align-items-stretch">
            {/* Location Card */}
            <div className="col-lg-6">
              <div
                className="new-footer-card info-card contact-hover-card"
                style={{ background: "#fbf4e6" }}
              >
                <div className="icon-circle" style={{ background: "#e6f7ff", color: "#1890ff" }}>
                  <EnvironmentFilled style={{ fontSize: "26px" }} />
                </div>

                <h3 className="new-footer-title">Our Locations</h3>

                <p className="new-footer-text">
                  <strong>Head Office (Dehradun, Uttarakhand)</strong> <br />
                  Dehradun, Uttarakhand <br />
                  India
                </p>

                <p className="new-footer-text">
                  <strong>India Office (Bihar)</strong> <br />
                  Hanuman Nagar, Near Collectorate <br />
                  Chapra, Saran, Bihar – 841301 <br />
                  India
                </p>

                <p className="new-footer-text">
                  <strong>US Office</strong> <br />
                  11322 Miramar Pkwy #1028 <br />
                  Miramar, FL 33025 <br />
                  United States
                </p>

                <p style={{ color: "#888", fontSize: "14px", marginTop: "10px" }}>
                  Visit us during business hours or schedule an appointment with
                  our team for an in-person consultation.
                </p>
              </div>
            </div>

            {/* Social Card */}
            <div className="col-lg-6">
              <div
                className="new-footer-card info-card contact-hover-card"
                style={{ background: "#b8f9d3" }}
              >
                <div className="icon-circle" style={{ background: "#f6ffed", color: "#52c41a" }}>
                  <MessageFilled style={{ fontSize: "26px" }} />
                </div>
                <h3 className="new-footer-title">Follow Us</h3>
                <p className="new-footer-text">
                  Stay connected with Stusphere for updates, insights, and
                  product announcements.
                </p>

                <div className="social-grid">
                  <a href="#" className="social-item" style={{ color: "#0077b5" }}>
                    <LinkedinFilled />
                  </a>
                  <a href="#" className="social-item" style={{ color: "#1DA1F2" }}>
                    <TwitterOutlined />
                  </a>
                  <a href="#" className="social-item" style={{ color: "#FF0000" }}>
                    <YoutubeFilled />
                  </a>
                  <a href="#" className="social-item" style={{ color: "#fa8c16" }}>
                    <ReadOutlined />
                  </a>
                </div>

                <p style={{ color: "#888", fontSize: "14px", marginTop: "20px" }}>
                  We typically respond within 24 hours on business days.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
};

export default ContactContainer;